import React, { useState } from 'react';
import { Alert, Button, Card, ProgressBar, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCheckCircle, 
  faExclamationTriangle, 
  faRedo, 
  faArrowRight,
  faInfoCircle,
  faCog
} from '@fortawesome/free-solid-svg-icons';
import { useWorkflow } from '../WorkflowProvider';
import { smartCMAHelper } from '../utils/SmartCMAHelper';

interface WorkflowProgressTrackerProps {
  onRecovery?: (step: number) => void;
  showRecoveryOptions?: boolean;
}

interface ProgressStepInfo {
  id: number;
  title: string;
  description: string;
  status: 'pending' | 'active' | 'completed' | 'error';
  canAdvance: boolean;
  errorMessage?: string;
  suggestions?: string[];
}

const WorkflowProgressTracker: React.FC<WorkflowProgressTrackerProps> = ({ 
  onRecovery, 
  showRecoveryOptions = true 
}) => {
  const { state, setStep, setError } = useWorkflow();
  const { currentStep, error, propertyInput, mlsData, predictionResult } = state;
  
  const [isRecovering, setIsRecovering] = useState(false);
  const [recoveryMessage, setRecoveryMessage] = useState('');

  // Define step information with dynamic status
  const getStepInfo = (): ProgressStepInfo[] => {
    return [
      {
        id: 1,
        title: 'Property Discovery',
        description: 'Enter property details and validate data',
        status: currentStep > 1 ? 'completed' : (currentStep === 1 ? 'active' : 'pending'),
        canAdvance: !!propertyInput?.property_address
      },
      {
        id: 2,
        title: 'Comparable Analysis',
        description: 'Find and analyze comparable properties',
        status: currentStep > 2 ? 'completed' : (currentStep === 2 ? 'active' : 'pending'),
        canAdvance: !!mlsData || !!propertyInput
      },
      {
        id: 3,
        title: 'AI Analysis',
        description: 'Generate ML prediction and insights',
        status: currentStep > 3 ? 'completed' : (currentStep === 3 ? 'active' : 'pending'),
        canAdvance: !!predictionResult || currentStep >= 3
      },
      {
        id: 4,
        title: 'Dashboard & Report',
        description: 'View comprehensive analysis and export',
        status: currentStep === 4 ? 'active' : (currentStep > 4 ? 'completed' : 'pending'),
        canAdvance: true
      }
    ];
  };

  const steps = getStepInfo();
  const progressPercentage = Math.max(0, Math.min(100, ((currentStep - 1) / 3) * 100));

  // Detect workflow issues
  const detectWorkflowIssues = (): { hasIssues: boolean; issues: string[] } => {
    const issues: string[] = [];

    // Check for common advancement issues
    if (currentStep === 2 && !propertyInput?.property_address) {
      issues.push('Property address missing - cannot proceed to comparable analysis');
    }

    if (currentStep === 3 && !mlsData && !sessionStorage.getItem('currentReportId')) {
      issues.push('No comparable data found - AI analysis cannot proceed');
    }

    if (currentStep === 4 && !predictionResult && !sessionStorage.getItem('currentReportId')) {
      issues.push('No prediction results available for dashboard');
    }

    // Check for database save issues based on error messages
    if (error && error.includes('required to return data')) {
      issues.push('Database save error detected - report may not be persisting');
    }

    return {
      hasIssues: issues.length > 0,
      issues
    };
  };

  const { hasIssues, issues } = detectWorkflowIssues();

  // Handle automatic recovery
  const handleAutoRecovery = async () => {
    setIsRecovering(true);
    setRecoveryMessage('Attempting to recover workflow...');

    try {
      const recoveryResult = await smartCMAHelper.recoverWorkflow(state);
      
      if (recoveryResult.success && recoveryResult.nextStep) {
        setRecoveryMessage(`Recovered! Advancing to step ${recoveryResult.nextStep}`);
        setError(undefined); // Clear any errors
        
        // Cast the nextStep to the correct type
        const validNextStep = recoveryResult.nextStep as 1 | 2 | 3 | 4;
        
        setTimeout(() => {
          setStep(validNextStep);
          onRecovery?.(recoveryResult.nextStep!);
          setIsRecovering(false);
          setRecoveryMessage('');
        }, 1500);
      } else {
        throw new Error('Recovery failed - no valid recovery path found');
      }
    } catch (error: any) {
      setRecoveryMessage('');
      setError(`Recovery failed: ${error.message}`);
      setIsRecovering(false);
    }
  };

  // Handle manual step advancement with validation
  const handleAdvanceToStep = async (targetStep: number) => {
    try {
      const targetStepInfo = steps.find(s => s.id === targetStep);
      
      if (!targetStepInfo?.canAdvance) {
        setError(`Cannot advance to ${targetStepInfo?.title} - missing required data`);
        return;
      }

      setError(undefined);
      
      // Validate step range and cast explicitly
      let validStep: 1 | 2 | 3 | 4;
      switch (targetStep) {
        case 1:
          validStep = 1;
          break;
        case 2:
          validStep = 2;
          break;
        case 3:
          validStep = 3;
          break;
        case 4:
          validStep = 4;
          break;
        default:
          throw new Error(`Invalid step number: ${targetStep}`);
      }
      
      setStep(validStep);
      onRecovery?.(targetStep);
    } catch (error: any) {
      setError(`Failed to advance to step ${targetStep}: ${error.message}`);
    }
  };

  const getStepStatusIcon = (step: ProgressStepInfo) => {
    if (step.status === 'completed') {
      return <FontAwesomeIcon icon={faCheckCircle} className="text-success" />;
    } else if (step.status === 'error') {
      return <FontAwesomeIcon icon={faExclamationTriangle} className="text-danger" />;
    } else if (step.status === 'active') {
      return <Spinner animation="border" size="sm" variant="primary" />;
    } else {
      return <div className="step-number">{step.id}</div>;
    }
  };

  return (
    <Card className="workflow-progress-tracker mb-4">
      <Card.Header>
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            <FontAwesomeIcon icon={faCog} className="me-2" />
            Smart CMA Progress
          </h5>
          <span className="badge bg-primary">{Math.round(progressPercentage)}% Complete</span>
        </div>
      </Card.Header>
      
      <Card.Body>
        {/* Overall Progress Bar */}
        <ProgressBar 
          now={progressPercentage} 
          className="mb-4"
          style={{ height: '8px' }}
          variant={hasIssues ? 'warning' : 'success'}
        />

        {/* Step Indicators */}
        <div className="row mb-4">
          {steps.map((step, index) => (
            <div key={step.id} className="col-md-3">
              <div 
                className={`step-indicator text-center p-3 rounded ${
                  step.status === 'active' ? 'bg-light border border-primary' :
                  step.status === 'completed' ? 'bg-light' : 'bg-light opacity-50'
                }`}
              >
                <div className="step-icon mb-2">
                  {getStepStatusIcon(step)}
                </div>
                <h6 className={`step-title ${step.status === 'active' ? 'text-primary fw-bold' : ''}`}>
                  {step.title}
                </h6>
                <small className="text-muted">{step.description}</small>
                
                {/* Manual advancement button for debugging */}
                {showRecoveryOptions && step.id !== currentStep && step.canAdvance && (
                  <Button 
                    variant="outline-secondary" 
                    size="sm" 
                    className="mt-2"
                    onClick={() => handleAdvanceToStep(step.id)}
                  >
                    <FontAwesomeIcon icon={faArrowRight} className="me-1" />
                    Go to Step {step.id}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Issues Detection */}
        {hasIssues && (
          <Alert variant="warning" className="mb-3">
            <Alert.Heading>
              <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
              Workflow Issues Detected
            </Alert.Heading>
            <ul className="mb-3">
              {issues.map((issue, index) => (
                <li key={index}>{issue}</li>
              ))}
            </ul>
            
            {showRecoveryOptions && (
              <div className="d-flex gap-2">
                <Button 
                  variant="warning" 
                  onClick={handleAutoRecovery}
                  disabled={isRecovering}
                >
                  {isRecovering ? (
                    <Spinner animation="border" size="sm" className="me-2" />
                  ) : (
                    <FontAwesomeIcon icon={faRedo} className="me-2" />
                  )}
                  Auto Recover
                </Button>
                
                <Button 
                  variant="outline-secondary"
                  onClick={() => window.location.reload()}
                >
                  Restart Workflow
                </Button>
              </div>
            )}
          </Alert>
        )}

        {/* Recovery Status */}
        {isRecovering && recoveryMessage && (
          <Alert variant="info">
            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
            {recoveryMessage}
          </Alert>
        )}

        {/* Current Status */}
        <div className="current-status">
          <h6>Current Status:</h6>
          <p className="text-muted mb-2">
            Step {currentStep} of 4: {steps.find(s => s.id === currentStep)?.title}
          </p>
          
          {/* Debug Information */}
          {showRecoveryOptions && (
            <details className="mt-3">
              <summary className="text-muted small" style={{ cursor: 'pointer' }}>
                Debug Information
              </summary>
              <div className="mt-2 p-2 bg-light rounded">
                <small className="text-muted">
                  <strong>Property Data:</strong> {propertyInput ? '✓' : '✗'}<br />
                  <strong>MLS Data:</strong> {mlsData ? '✓' : '✗'}<br />
                  <strong>Prediction Result:</strong> {predictionResult ? '✓' : '✗'}<br />
                  <strong>Session Report ID:</strong> {sessionStorage.getItem('currentReportId') || 'None'}<br />
                  <strong>Current Error:</strong> {error || 'None'}
                </small>
              </div>
            </details>
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

export default WorkflowProgressTracker; 